import { Edit3, Eye, Leaf, PackageMinus, PackagePlus, Trash2, History, QrCode, Copy, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { TablePagination } from '@/components/ui/TablePagination';
import { formatBillionCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { InventoryItem } from '../types/inventoryList.types';
import { InventoryStatusBadge } from './inventoryStatus';

interface InventoryTableProps {
  items: InventoryItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  selectedId?: string;
  onSelect: (item: InventoryItem) => void;
  onView: (id: string) => void;
  onDelete: (item: InventoryItem) => void;
  onEdit: (id: string) => void;
  canDelete: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function InventoryTable({
  items,
  total,
  page,
  pageSize,
  isLoading,
  selectedId,
  onSelect,
  onView,
  onDelete,
  onEdit,
  canDelete,
  onPageChange,
  onPageSizeChange,
}: InventoryTableProps) {
  const navigate = useNavigate();
  return (
    <div className="min-h-[520px] flex-1 flex-col flex bg-white">
      <div className="flex-1 overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500">
              <th className="w-12 p-3 text-center font-normal"><input type="checkbox" className="rounded-md border-slate-300" /></th>
              <th className="p-3 text-center font-normal">Mã vật tư</th>
              <th className="p-3 text-left font-normal">Tên vật tư</th>
              <th className="p-3 text-center font-normal">Nhóm vật tư</th>
              <th className="p-3 text-center font-normal">Đơn vị tính</th>
              <th className="p-3 text-center font-normal">Tồn kho</th>
              <th className="p-3 text-center font-normal">Giá nhập</th>
              <th className="p-3 text-center font-normal">Giá trị tồn kho</th>
              <th className="p-3 text-center font-normal">Trạng thái</th>
              <th className="p-3 text-center font-normal">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <tr key={index} className="animate-pulse">
                  <td colSpan={10} className="p-3"><div className="h-10 rounded-md bg-slate-100" /></td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-10 text-center text-xs text-slate-500">Không có vật tư phù hợp.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => onSelect(item)}
                  className={cn(
                    'cursor-pointer hover:bg-slate-50 transition-colors',
                    selectedId === item.id && 'bg-emerald-50/50'
                  )}
                >
                  <td className="p-3 text-center">
                    <input type="checkbox" className="rounded-md border-slate-300" onClick={(event) => event.stopPropagation()} />
                  </td>
                  <td className="p-3 text-center text-slate-900 font-medium">
                    {item.code}
                  </td>
                  <td className="p-3 text-left text-slate-900">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-100 text-emerald-600">
                        <img 
                          src={item.thumbnail_url || '/assets/other/img/inventory.png'} 
                          alt={item.name} 
                          className="h-full w-full object-cover" 
                        />
                      </div>
                      <span className="font-medium truncate max-w-[200px]">{item.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-center text-slate-700">
                    {item.category_name || 'Khác'}
                  </td>
                  <td className="p-3 text-center text-slate-700">
                    {item.unit}
                  </td>
                  <td className="p-3 text-center text-slate-900 font-medium">
                    {item.current_stock}
                  </td>
                  <td className="p-3 text-center text-slate-700">
                    {item.unit_price.toLocaleString('vi-VN')}
                  </td>
                  <td className="p-3 text-center text-slate-700">
                    {(item.current_stock * item.unit_price).toLocaleString('vi-VN')}
                  </td>
                  <td className="p-3 text-center">
                    <InventoryStatusBadge status={item.status_code} />
                  </td>
                  <td className="p-3 text-center" onClick={(event) => event.stopPropagation()}>
                    <div className="flex justify-center">
                      <ActionMenu
                        items={[
                          { label: 'Xem chi tiết', icon: Eye, onClick: () => onView(item.id) },
                          { label: 'Sửa', icon: Edit3, onClick: () => onEdit(item.id) },
                          { label: 'Nhập kho', icon: PackagePlus, onClick: () => navigate(`/inventory/requests/new?type=import&itemId=${item.id}`) },
                          { label: 'Xuất kho', icon: PackageMinus, onClick: () => navigate(`/inventory/requests/new?type=export&itemId=${item.id}`) },
                          { label: 'Nhân bản', icon: Copy, onClick: () => console.log('clone', item.id) },
                          { label: 'Khóa', icon: Lock, onClick: () => console.log('lock', item.id) },
                          ...(canDelete ? [{ label: 'Xóa', icon: Trash2, destructive: true, onClick: () => onDelete(item) }] : []),
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-auto border-t border-slate-200 px-3">
        <TablePagination
          currentPage={page}
          totalItems={total}
          pageSize={pageSize}
          itemLabel="vật tư "
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizeOptions={[10, 20, 50]}
          className="flex-1"
        />
      </div>
      <span className="sr-only">Tổng giá trị tồn kho theo tỷ ng: {formatBillionCurrency(items.reduce((sum, item) => sum + item.current_stock * item.unit_price, 0))}</span>
    </div>
  );
}
